import { useContext, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT, Var } from 'gt-react';
import { UserSwitchIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { BATCH_ASSIGN_TASKS_MAX, type BatchAssignTasksInput } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';
import { useAllAgentDefinitions } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useUsersInOrg } from 'src/api/rpc/hooks/core/useUsersInOrg';
import { invalidateTaskQueries } from 'src/api/rpc/hooks/tasks/invalidateTaskQueries';
import { SingleSelect } from 'src/components/common/SingleSelect';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { toast } from 'src/components/ui/toast';
import { AuthContext } from 'src/context/auth.context';
import { useCan } from 'src/hooks/useCan';
import type { SingleSelectOption } from 'src/components/common/SingleSelect';
import {
  batchAssignInChunks,
  buildReassignAssigneeOptions,
  reassignResultMessage,
  reassignTargetFromOptionId,
} from './taskQueueReassign';

/**
 * Pick one person or agent (or clear the assignee) and apply that to every
 * selected open task. Closed tasks are not in `taskIds`.
 */
export function TaskQueueReassignDialog({
  open,
  onOpenChange,
  taskIds,
  onAssigned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskIds: readonly string[];
  onAssigned: () => void;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const options = useReassignAssigneeOptions(open);
  const [selected, setSelected] = useState<SingleSelectOption | null>(null);
  const batchAssign = useMutation({
    mutationFn: (input: BatchAssignTasksInput) =>
      batchAssignInChunks(input, (chunk) => rpcClient.tasks.batchAssign(chunk)),
    // Settled, not success: when one batch fails, others may have assigned.
    onSettled: () => {
      invalidateTaskQueries(queryClient);
    },
  });
  const isBusy = batchAssign.isPending;

  function close() {
    if (isBusy) return;
    setSelected(null);
    onOpenChange(false);
  }

  async function handleConfirm() {
    if (!selected) return;
    const target = reassignTargetFromOptionId(selected.id);
    if (!target || taskIds.length === 0) return;
    try {
      const result = await batchAssign.mutateAsync({
        taskIds: [...taskIds],
        ...target,
      });
      setSelected(null);
      onOpenChange(false);
      onAssigned();
      toast.success(reassignResultMessage(gt, result));
    } catch (error) {
      const reason =
        error instanceof Error
          ? error.message
          : gt('Could not reassign the selected tasks', {
              context: 'toast after a mass-reassign failed',
            });
      toast.error(
        taskIds.length > BATCH_ASSIGN_TASKS_MAX
          ? gt(
              '{reason} Some of the tasks may already be reassigned; check the list before trying again.',
              {
                context:
                  'toast after a large mass-reassign failed partway; reason is the error message',
                reason,
              },
            )
          : reason,
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, mass-reassign tasks from a task page">
              Reassign tasks
            </T>
          </DialogTitle>
          <DialogDescription>
            {taskIds.length === 1 ? (
              <T context="dialog description, mass-reassign a single open task">
                Move this open task to one person or agent. Closed tasks in the
                selection stay as they are.
              </T>
            ) : (
              <T context="dialog description, mass-reassign several open tasks">
                Move <Var>{taskIds.length}</Var> open tasks to one person or
                agent. Closed tasks in the selection stay as they are.
              </T>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-queue-reassign-assignee">
            <T context="form label, mass-reassign target">Assign to</T>
          </Label>
          <SingleSelect
            id="task-queue-reassign-assignee"
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder={gt('Select a person or agent', {
              context: 'placeholder on the task mass-reassign picker',
            })}
            searchPlaceholder={gt('Search…', {
              context: 'search box in the task mass-reassign picker',
            })}
            fullWidth
            disabled={isBusy}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={isBusy}>
            <T context="button, cancel mass-reassign">Cancel</T>
          </Button>
          <Button onClick={handleConfirm} disabled={isBusy || !selected}>
            <UserSwitchIcon className="h-4 w-4" />
            <T context="button, confirm mass-reassign of selected tasks">
              Reassign
            </T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useReassignAssigneeOptions(open: boolean): SingleSelectOption[] {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);
  const { data: users } = useUsersInOrg();
  const { data: agents } = useAllAgentDefinitions({
    enabled: open && canReadAgents,
  });
  return buildReassignAssigneeOptions({
    gt,
    users: users ?? [],
    agents: canReadAgents ? (agents?.definitions ?? []) : [],
    currentUserId: currentUser?.id,
  });
}
